import KenshinTable from 'components/KenshinTable';
import { TitleColumn, CreatedAtColumn, OthersColumn } from '../commonColumns';
import { useMemo, useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { getFirstDayPreviousMonth } from 'utils/dateTime';
import { USER_DATA } from 'shared/constants/users';
import localData from 'utils/localData';
import { USER_COMMENT } from 'shared/constants/comment';
import { KENSHIN_API } from 'shared/constants/apis';
import PropTypes from 'prop-types';
import useApi from 'shared/hooks/api';
import { PageLoader, PageError } from 'components';
import { updateArrayItemById } from 'utils/javascript';
import intl from 'react-intl-universal';
import { KenshinIssueStatusForClient } from 'shared/constants/kenshin/issues';
import { SentProgressColumn, ToColumn } from './columns';
import logger from 'utils/logger';
import { TABLE_STATE_KEYS } from 'shared/constants/tableOptions';
const propTypes = {
  type: PropTypes.string,
};

const log = logger.createLogger('SentKenshin');

const SentKenshin = ({ type }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const currentDate = new Date();
  currentDate.setHours(23, 59, 59, 999);
  const firstDayPreviousMonth = getFirstDayPreviousMonth(currentDate);
  const [timeRangeStart, setTimeRangeStart] = useState(() => {
    const saved = sessionStorage.getItem(TABLE_STATE_KEYS.KENSHIN_START_DATE);
    return saved ? new Date(saved) : firstDayPreviousMonth;
  });
  const [timeRangeEnd, setTimeRangeEnd] = useState(() => {
    const saved = sessionStorage.getItem(TABLE_STATE_KEYS.KENSHIN_END_DATE);
    return saved ? new Date(saved) : currentDate;
  });

  useEffect(() => {
    if (location.state?.shouldRefresh) {
      fetchProject();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location]);

  useEffect(() => {
    sessionStorage.setItem(
      TABLE_STATE_KEYS.KENSHIN_START_DATE,
      timeRangeStart.toISOString()
    );
  }, [timeRangeStart]);

  useEffect(() => {
    sessionStorage.setItem(
      TABLE_STATE_KEYS.KENSHIN_END_DATE,
      timeRangeEnd.toISOString()
    );
  }, [timeRangeEnd]);

  const handleOpenIssueDetails = (issueId) => {
    navigate(`${location.pathname}/issues/${issueId}`);
  };

  const columns = useMemo(
    () => [
      TitleColumn(),
      SentProgressColumn(),
      CreatedAtColumn(),
      ToColumn(),
      OthersColumn({ handleOpenIssueDetails }),
    ],
    []
  );

  const currentUsername = localData.get(USER_DATA.USERNAME);
  const userId = localData.get(USER_COMMENT.USER_ID);

  const [{ data, error, setLocalData }, fetchProject] = useApi.useGet(
    KENSHIN_API.ISSUES_SENT,
    {
      params: {
        organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
        type: type,
        timeRangeStart: timeRangeStart,
        timeRangeEnd: timeRangeEnd,
        username: currentUsername,
        userId: userId,
      },
      baseURL: import.meta.env.VITE_KENSHIN_API_URL,
    }
  );
  if (!data) return <PageLoader />;
  if (error)
    return (
      <PageError
        errorCode={error.response.status}
        customErrorMessage={error.response.data.message}
        customErrorDescription={error.response.data.description}
      />
    );

  const { sentIssues, doctors } = data;

  const updateLocalProjectIssues = (issueId, updatedFields) => {
    setLocalData((currentData) => ({
      sentIssues: updateArrayItemById(
        currentData.sentIssues,
        issueId,
        updatedFields
      ),
      doctors: doctors,
    }));
    // the function passed into the brackets of setLocalData(...) is the `getUpdatedData` in setLocalData in /shared/hooks/api/query.js;
    // Therefore currentData is `data` in the line `const updatedData = getUpdatedData(data);`;
  };

  const handleRefreshClick = async () => {
    // Updating search times as the table will contain the same issues if time is not updated
    if (timeRangeEnd.toDateString() === new Date().toDateString()) {
      setTimeRangeEnd(new Date());
    }
    try {
      await fetchProject();
      enqueueSnackbar(intl.get('shared_table_data_reload_success'), {
        variant: 'success',
        autoHideDuration: 3000,
      });
    } catch (error) {
      log.error('Error in handleRefreshClick', { error });
      enqueueSnackbar(intl.get('shared_table_data_reload_error'), {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  sentIssues.sort((issue1, issue2) => issue2.id - issue1.id);
  const tableAllData = sentIssues.map((issue, index) => {
    return {
      index: index,
      title: issue.title,
      progress: KenshinIssueStatusForClient[issue.progress],
      createdAt: issue.createdAt,
      dstOrgName: issue.dstOrgName,
      id: issue.id,
    };
  });

  return (
    <div>
      <KenshinTable
        columns={columns}
        tableTitle={intl.get('kenshin_sent_history_table_title')}
        timeRangeStart={timeRangeStart}
        timeRangeEnd={timeRangeEnd}
        updateTimeRangeStart={setTimeRangeStart}
        updateTimeRangeEnd={setTimeRangeEnd}
        data={tableAllData}
        refreshData={handleRefreshClick}
        handleBatchEditClick={null}
      />
      <Outlet
        context={{
          doctors,
          updateLocalProjectIssues,
        }}
      />
    </div>
  );
};

SentKenshin.propTypes = propTypes;

export default SentKenshin;
